import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { ApiError, type ApiResponse } from '@/types/api';

/**
 * Shared Axios instance configured to:
 *  - Send cookies on every request (for PHP session auth)
 *  - Mark requests as XHR so PHP recognizes them as AJAX
 *  - Unwrap the ApiResponse envelope into just `data`
 *  - Throw ApiError when the envelope reports {success: false}
 *  - Emit an 'unauthorized' event on 401 so the app can redirect to login
 */

type UnauthorizedHandler = () => void;

const unauthorizedHandlers = new Set<UnauthorizedHandler>();

export function onUnauthorized(handler: UnauthorizedHandler): () => void {
  unauthorizedHandlers.add(handler);
  return () => unauthorizedHandlers.delete(handler);
}

function createClient(): AxiosInstance {
  const client = axios.create({
    baseURL: '/',
    withCredentials: true,
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      Accept: 'application/json',
    },
  });

  client.interceptors.response.use(
    (response) => {
      const body = response.data as ApiResponse<unknown>;
      // Empty or non-envelope responses to /request/* endpoints mean the
      // backend crashed before it could serialize an envelope — typically
      // an uncaught exception swallowed by a global handler, leaving a
      // 200 with Content-Length: 0. Surface that as a clear error instead
      // of silently returning an empty string that callers misinterpret.
      const url = response.config?.url ?? '';
      if (
        url.startsWith('/request/')
        || url.includes('Api/')
      ) {
        if (!body || typeof body !== 'object' || !('success' in body)) {
          const fallback: ApiResponse<unknown> = {
            success: false,
            msg: 'Server returned an empty response',
            status: { code: response.status, message: 'Empty response' },
            formattedErrors: false,
            errors: false,
            warnings: false,
            data: null,
            messages: [],
          };
          throw new ApiError(fallback.msg, fallback);
        }
      } else if (!body || typeof body !== 'object' || !('success' in body)) {
        // Non-API responses (static HTML, blobs, etc.) pass through untouched.
        return response;
      }
      if (body.success === false) {
        throw new ApiError(body.msg || 'API request failed', body);
      }
      // PHP's requestController sets `data = $requestData ?: ''`, which
      // means endpoints that legitimately return an empty array come
      // back as an empty string. Coerce that to null so callers get a
      // predictable value to `?? []` against — without this, downstream
      // `.filter()` / `.map()` calls on list responses explode with a
      // generic TypeError that surfaces as "Failed to load <thing>".
      response.data = body.data === '' ? null : body.data;
      return response;
    },
    (error) => {
      if (error.response?.status === 401) {
        for (const handler of unauthorizedHandlers) handler();
      }
      return Promise.reject(error);
    },
  );

  return client;
}

export const api = createClient();

/**
 * PHP's legacy /request/ endpoints expect POST with form-encoded bodies.
 * The "action" is part of the URL: /request/<apiName>/<actionName>.
 */
export async function callApi<TData = unknown>(
  apiName: string,
  action: string,
  params: Record<string, unknown> = {},
  config?: AxiosRequestConfig,
): Promise<TData> {
  const url = `/request/${apiName}/${action}`;
  const form = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    form.append(key, typeof value === 'string' ? value : JSON.stringify(value));
  }
  const response = await api.post<TData>(url, form, {
    ...config,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      ...(config?.headers || {}),
    },
  });
  return response.data as TData;
}

/**
 * Newer /Dashboard/Api/* endpoints accept JSON bodies.
 */
export async function callDashboardApi<TData = unknown>(
  path: string,
  params: Record<string, unknown> = {},
  config?: AxiosRequestConfig,
): Promise<TData> {
  const url = path.startsWith('/') ? path : `/${path}`;
  const response = await api.post<TData>(url, params, config);
  return response.data as TData;
}

/**
 * MVC-style controllers (e.g. `/ProctorToolsApi/...`) populate `$this->params`
 * from `$_POST` only — they do NOT read JSON request bodies. The framework
 * supports a single `params` form field carrying the full JSON payload, which
 * keeps nested objects, numbers, and booleans typed correctly.
 */
export async function callMvcApi<TData = unknown>(
  path: string,
  params: Record<string, unknown> = {},
  config?: AxiosRequestConfig,
): Promise<TData> {
  const url = path.startsWith('/') ? path : `/${path}`;
  const form = new URLSearchParams();
  form.append('params', JSON.stringify(params));
  const response = await api.post<TData>(url, form, {
    ...config,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      ...(config?.headers || {}),
    },
  });
  return response.data as TData;
}
